/**
 * Platform Admin API Service
 * Handles all API calls for the platform admin console
 * Uses a dedicated axios instance without tenant interceptors
 */

import axios from 'axios';

// Create a dedicated axios instance for platform API
const platformAxios = axios.create({
  baseURL: '/content-management-api',
  headers: {
    'Content-Type': 'application/json',
  },
});

const PLATFORM_API_BASE = '/platform';
const PLATFORM_AUTH_BASE = '/auth/platform';

/** Paths where 401 means bad credentials / challenge, not an expired session. */
const PLATFORM_AUTH_CREDENTIAL_401_PATHS = [
  `${PLATFORM_AUTH_BASE}/login`,
  `${PLATFORM_AUTH_BASE}/login/verify-otp`,
  `${PLATFORM_AUTH_BASE}/change-password`,
  `${PLATFORM_AUTH_BASE}/change-password/verify-otp`,
  `${PLATFORM_AUTH_BASE}/forgot-password`,
  `${PLATFORM_AUTH_BASE}/reset-password`,
  `${PLATFORM_AUTH_BASE}/resend-verification`,
  `${PLATFORM_AUTH_BASE}/register`,
  `${PLATFORM_AUTH_BASE}/verify`,
];

/**
 * Whether a failed request should trigger a single token refresh retry.
 * Exported for unit tests.
 *
 * @param {import('axios').AxiosError} error
 * @param {boolean} alreadyRetried
 * @returns {boolean}
 */
export function shouldAttemptPlatformRefresh(error, alreadyRetried) {
  if (alreadyRetried) return false;
  if (error?.response?.status !== 401) return false;

  const url = error?.config?.url || '';
  // Do not refresh for credential/challenge auth endpoints or refresh itself
  if (PLATFORM_AUTH_CREDENTIAL_401_PATHS.some((path) => url.includes(path))) {
    return false;
  }
  if (url.includes(`${PLATFORM_AUTH_BASE}/refresh`)) {
    return false;
  }

  const refreshToken = typeof localStorage !== 'undefined'
    ? localStorage.getItem('platform_refresh_token')
    : null;
  return Boolean(refreshToken);
}

function clearPlatformAuthStorage() {
  localStorage.removeItem('platform_token');
  localStorage.removeItem('platform_refresh_token');
  localStorage.removeItem('platform_user');
}

function redirectToPlatformLogin() {
  if (!window.location.pathname.startsWith('/platform/login')) {
    window.location.href = '/platform/login';
  }
}

function isCredentialPlatformAuthUrl(url = '') {
  return PLATFORM_AUTH_CREDENTIAL_401_PATHS.some((path) => url.includes(path));
}

// Add auth token interceptor
platformAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem('platform_token');
  const configToReturn = { ...config };
  if (token) {
    configToReturn.headers.Authorization = `Bearer ${token}`;
  }
  return configToReturn;
});

// Handle auth errors: one refresh retry, then clear + redirect
platformAxios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};
    const alreadyRetried = Boolean(originalRequest._platformRefreshRetried);

    if (shouldAttemptPlatformRefresh(error, alreadyRetried)) {
      originalRequest._platformRefreshRetried = true;
      try {
        const refreshToken = localStorage.getItem('platform_refresh_token');
        const refreshResponse = await platformAxios.post(`${PLATFORM_AUTH_BASE}/refresh`, {
          refresh_token: refreshToken,
        });
        const { token, refresh_token: newRefreshToken } = refreshResponse.data || {};
        if (!token || !newRefreshToken) {
          throw new Error('Invalid refresh response');
        }
        localStorage.setItem('platform_token', token);
        localStorage.setItem('platform_refresh_token', newRefreshToken);
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return platformAxios(originalRequest);
      } catch (refreshError) {
        clearPlatformAuthStorage();
        redirectToPlatformLogin();
        return Promise.reject(refreshError);
      }
    }

    if (error.response?.status === 401) {
      const url = originalRequest.url || '';
      // Wrong current password / login failures: show in form, keep session.
      if (isCredentialPlatformAuthUrl(url)) {
        return Promise.reject(error);
      }
      clearPlatformAuthStorage();
      redirectToPlatformLogin();
    }

    return Promise.reject(error);
  },
);

export const platformApi = {
  // ============================================
  // Platform Auth (unauthenticated unless noted)
  // ============================================

  async login(email, password) {
    const response = await platformAxios.post(`${PLATFORM_AUTH_BASE}/login`, { email, password });
    return response.data;
  },

  async register(userData) {
    const response = await platformAxios.post(`${PLATFORM_AUTH_BASE}/register`, userData);
    return response.data;
  },

  async verify(email, validationKey) {
    const response = await platformAxios.post(`${PLATFORM_AUTH_BASE}/verify`, {
      email,
      validationKey,
    });
    return response.data;
  },

  async verifyOtp(email, otp) {
    const response = await platformAxios.post(`${PLATFORM_AUTH_BASE}/login/verify-otp`, {
      email,
      otp,
    });
    return response.data;
  },

  async forgotPassword(email) {
    const response = await platformAxios.post(`${PLATFORM_AUTH_BASE}/forgot-password`, { email });
    return response.data;
  },

  async resetPassword({ email, password, validationKey }) {
    const response = await platformAxios.post(`${PLATFORM_AUTH_BASE}/reset-password`, {
      email,
      password,
      validationKey,
    });
    return response.data;
  },

  async resendVerification(email) {
    const response = await platformAxios.post(`${PLATFORM_AUTH_BASE}/resend-verification`, { email });
    return response.data;
  },

  async refresh(refreshToken) {
    const response = await platformAxios.post(`${PLATFORM_AUTH_BASE}/refresh`, {
      refresh_token: refreshToken,
    });
    return response.data;
  },

  /** Authenticated — step 1: verify current password, email OTP */
  async changePassword(currentPassword, newPassword) {
    const response = await platformAxios.post(`${PLATFORM_AUTH_BASE}/change-password`, {
      currentPassword,
      newPassword,
    });
    return response.data;
  },

  /** Authenticated — step 2: confirm OTP and apply new password */
  async confirmChangePassword(otp, currentPassword, newPassword) {
    const response = await platformAxios.post(`${PLATFORM_AUTH_BASE}/change-password/verify-otp`, {
      otp,
      currentPassword,
      newPassword,
    });
    return response.data;
  },

  // Stats
  async getStats() {
    const response = await platformAxios.get(`${PLATFORM_API_BASE}/stats`);
    return response.data?.data;
  },

  // Tenants
  async getTenants(params = {}) {
    const response = await platformAxios.get(`${PLATFORM_API_BASE}/tenants`, { params });
    // Response format: { success: true, count: N, data: [...] }
    const { data } = response;
    if (Array.isArray(data?.data)) {
      return data.data;
    }
    return Array.isArray(data) ? data : [];
  },

  async getTenant(slug) {
    const response = await platformAxios.get(`${PLATFORM_API_BASE}/tenants/${slug}`);
    return response.data?.data;
  },

  async createTenant(tenantData) {
    const response = await platformAxios.post(`${PLATFORM_API_BASE}/tenants`, tenantData);
    return response.data?.data;
  },

  async updateTenant(slug, tenantData) {
    const response = await platformAxios.put(`${PLATFORM_API_BASE}/tenants/${slug}`, tenantData);
    return response.data?.data;
  },

  async getTenantMcpAccess(slug) {
    const response = await platformAxios.get(`${PLATFORM_API_BASE}/tenants/${slug}/mcp-access`);
    return response.data?.data;
  },

  async getTenantCoachingLlm(slug) {
    const response = await platformAxios.get(`${PLATFORM_API_BASE}/tenants/${slug}/coaching-llm`);
    return response.data?.data;
  },

  async getTenantConfig(slug) {
    const response = await platformAxios.get(`${PLATFORM_API_BASE}/tenants/${slug}/config`);
    return response.data?.data;
  },

  async updateTenantConfig(slug, configData) {
    const response = await platformAxios.patch(`${PLATFORM_API_BASE}/tenants/${slug}/config`, configData);
    return response.data;
  },

  async deleteTenant(slug) {
    const response = await platformAxios.delete(`${PLATFORM_API_BASE}/tenants/${slug}`);
    return response.data;
  },

  async suspendTenant(slug) {
    const response = await platformAxios.post(`${PLATFORM_API_BASE}/tenants/${slug}/suspend`);
    return response.data?.data;
  },

  async activateTenant(slug) {
    const response = await platformAxios.post(`${PLATFORM_API_BASE}/tenants/${slug}/activate`);
    return response.data?.data;
  },

  /** Tenant roles migration from legacy config lists (platform admin). */
  async migrateTenantRoles(slug, { dryRun = true, reason } = {}) {
    const response = await platformAxios.post(
      `${PLATFORM_API_BASE}/tenants/${slug}/migrate/roles`,
      { dryRun, ...(reason ? { reason } : {}) },
    );
    return response.data;
  },

  /** Features collection cleanup migration (platform admin). */
  async migrateTenantFeatures(slug, { dryRun = true, reason } = {}) {
    const response = await platformAxios.post(
      `${PLATFORM_API_BASE}/tenants/${slug}/migrate/features`,
      { dryRun, ...(reason ? { reason } : {}) },
    );
    return response.data;
  },

  /** Seed customer navigation/home layouts (platform admin). */
  async seedTenantCustomerLayouts(slug, { dryRun = true, reason } = {}) {
    const response = await platformAxios.post(
      `${PLATFORM_API_BASE}/tenants/${slug}/seed/customer-layouts`,
      { dryRun, ...(reason ? { reason } : {}) },
    );
    return response.data;
  },

  // Platform Users
  async getUsers() {
    const response = await platformAxios.get(`${PLATFORM_API_BASE}/users`);
    return response.data?.data;
  },

  async createUser(userData) {
    const response = await platformAxios.post(`${PLATFORM_API_BASE}/users`, userData);
    return response.data?.data;
  },

  async updateUser(userId, userData) {
    const response = await platformAxios.patch(`${PLATFORM_API_BASE}/users/${userId}`, userData);
    return response.data?.data;
  },

  async updateUserAssignments(userId, tenantSlugs) {
    const response = await platformAxios.put(`${PLATFORM_API_BASE}/users/${userId}/assignments`, { tenantSlugs });
    return response.data?.data;
  },

  async deactivateUser(userId) {
    return this.updateUser(userId, { isActive: false });
  },

  async activateUser(userId) {
    return this.updateUser(userId, { isActive: true });
  },

  // Audit Logs
  async getAuditLogs(params = {}) {
    const response = await platformAxios.get(`${PLATFORM_API_BASE}/audit`, { params });
    return response.data?.data;
  },

  // Settings
  async getSettings() {
    const response = await platformAxios.get(`${PLATFORM_API_BASE}/settings`);
    return response.data?.data;
  },

  async updateSettings(settings) {
    const response = await platformAxios.patch(`${PLATFORM_API_BASE}/settings`, settings);
    return response.data?.data;
  },

  // Platform MCP tokens (platform_admin DB; secret prefix mcp_plat_)
  async getPlatformMcpTokens() {
    const response = await platformAxios.get(`${PLATFORM_API_BASE}/mcp-tokens`);
    return Array.isArray(response.data) ? response.data : [];
  },

  async createPlatformMcpToken(body) {
    const response = await platformAxios.post(`${PLATFORM_API_BASE}/mcp-tokens`, body);
    return response.data;
  },

  async revokePlatformMcpToken(id) {
    const response = await platformAxios.delete(`${PLATFORM_API_BASE}/mcp-tokens/${id}`);
    return response.data;
  },

  async deletePlatformMcpTokenPermanent(id) {
    const response = await platformAxios.delete(`${PLATFORM_API_BASE}/mcp-tokens/${id}/permanent`);
    return response.data;
  },

  /** MCP OAuth clients (platform_admin.mcp_oauth_clients) — redirect URI allowlist for tenant OAuth. */
  async getPlatformMcpOauthClients() {
    const response = await platformAxios.get(`${PLATFORM_API_BASE}/mcp-oauth-clients`);
    return response.data;
  },

  async createPlatformMcpOauthClient(body) {
    const response = await platformAxios.post(`${PLATFORM_API_BASE}/mcp-oauth-clients`, body);
    return response.data;
  },

  async updatePlatformMcpOauthClient(id, body) {
    const response = await platformAxios.patch(`${PLATFORM_API_BASE}/mcp-oauth-clients/${id}`, body);
    return response.data;
  },

  async deletePlatformMcpOauthClient(id) {
    const response = await platformAxios.delete(`${PLATFORM_API_BASE}/mcp-oauth-clients/${id}`);
    return response.data;
  },

  /** Platform MCP prompts (markdown bodies; published prompts register on MCP for this platform user). */
  async getPlatformMcpPrompts() {
    const response = await platformAxios.get(`${PLATFORM_API_BASE}/mcp-prompts`);
    return Array.isArray(response.data) ? response.data : [];
  },

  async createPlatformMcpPrompt(body) {
    const response = await platformAxios.post(`${PLATFORM_API_BASE}/mcp-prompts`, body);
    return response.data;
  },

  async updatePlatformMcpPrompt(id, body) {
    const response = await platformAxios.patch(`${PLATFORM_API_BASE}/mcp-prompts/${id}`, body);
    return response.data;
  },

  async deletePlatformMcpPrompt(id) {
    const response = await platformAxios.delete(`${PLATFORM_API_BASE}/mcp-prompts/${id}`);
    return response.data;
  },

  // Health Check
  async checkHealth() {
    const response = await platformAxios.get(`${PLATFORM_API_BASE}/health`);
    return response.data?.data;
  },

  // ============================================
  // Tenant Import/Export
  // ============================================

  /**
   * Validate an import ZIP file before creating tenant
   * @param {FormData} formData - FormData containing the 'file' field
   * @returns {Promise<{valid: boolean, errors: string[], warnings: string[], stats?: object}>}
   */
  async validateImport(formData) {
    const response = await platformAxios.post(`${PLATFORM_API_BASE}/tenants/validate-import`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * Create tenant with import from ZIP
   * @param {FormData} formData - FormData containing file, slug, name, adminEmail, plan
   * @returns {Promise<object>}
   */
  async importTenant(formData) {
    const response = await platformAxios.post(`${PLATFORM_API_BASE}/tenants/import`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * Export tenant data as ZIP file
   * @param {string} slug - Tenant slug
   * @returns {Promise<Blob>} - ZIP file blob for download
   */
  async exportTenant(slug) {
    const response = await platformAxios.get(`${PLATFORM_API_BASE}/tenants/${slug}/export`, {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Download tenant export (triggers browser download)
   * @param {string} slug - Tenant slug
   */
  async downloadTenantExport(slug) {
    const blob = await this.exportTenant(slug);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `tenant-export-${slug}-${new Date().toISOString().split('T')[0]}.zip`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },
};

export default platformApi;
